import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration from './payments-payment-acceptance-configurations-v1-payment-acceptance-configuration-payment-acceptance-configurations.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** PaymentAcceptanceConfiguration */
export interface PaymentAcceptanceConfiguration {
  /**
   * Id
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * App id
   * @format GUID
   * @immutable
   */
  appId?: string | null;
  /**
   * Rules
   * @maxSize 1000
   */
  rules?: PaymentAcceptanceRule[];
  /**
   * Revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Date when PaymentAcceptanceConfiguration was created
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Date when PaymentAcceptanceConfiguration was updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

export interface PaymentAcceptanceRule {
  /**
   * Rule Id
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Payment method type id
   * @format GUID
   */
  paymentMethodTypeId?: string;
  /**
   * Account connection id
   * @format GUID
   */
  accountConnectionId?: string;
  /** Merchant enabled */
  merchantEnabled?: boolean;
}

export interface PaymentBrands {
  /**
   * Payment brand ids
   * @maxSize 200
   * @format GUID
   */
  paymentBrandIds?: string[];
}

export interface Countries {
  /**
   * Country codes
   * @maxSize 200
   * @format COUNTRY
   */
  countryCodes?: string[];
}

export interface Currencies {
  /**
   * Currency codes
   * @maxSize 200
   * @format CURRENCY
   */
  currencyCodes?: string[];
}

export interface Restrictions
  extends RestrictionsPaymentBrandsOneOf,
    RestrictionsCountriesOneOf,
    RestrictionsCurrenciesOneOf {
  /** Allowed payment brands */
  allowedPaymentBrands?: PaymentBrands;
  /** Prohibited payment brands */
  prohibitedPaymentBrands?: PaymentBrands;
  /** Allowed countries */
  allowedCountries?: Countries;
  /** Prohibited countries */
  prohibitedCountries?: Countries;
  /** Allowed currencies */
  allowedCurrencies?: Currencies;
  /** Prohibited currencies */
  prohibitedCurrencies?: Currencies;
}

/** @oneof */
export interface RestrictionsPaymentBrandsOneOf {
  /** Allowed payment brands */
  allowedPaymentBrands?: PaymentBrands;
  /** Prohibited payment brands */
  prohibitedPaymentBrands?: PaymentBrands;
}

/** @oneof */
export interface RestrictionsCountriesOneOf {
  /** Allowed countries */
  allowedCountries?: Countries;
  /** Prohibited countries */
  prohibitedCountries?: Countries;
}

/** @oneof */
export interface RestrictionsCurrenciesOneOf {
  /** Allowed currencies */
  allowedCurrencies?: Currencies;
  /** Prohibited currencies */
  prohibitedCurrencies?: Currencies;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface CreatePaymentAcceptanceConfigurationRequest {
  /** PaymentAcceptanceConfiguration to be created. */
  paymentAcceptanceConfiguration: PaymentAcceptanceConfiguration;
}

export interface CreatePaymentAcceptanceConfigurationResponse {
  /** The created PaymentAcceptanceConfiguration. */
  paymentAcceptanceConfiguration?: PaymentAcceptanceConfiguration;
}

export interface GetPaymentAcceptanceConfigurationRequest {
  /**
   * ID of the PaymentAcceptanceConfiguration to retrieve.
   * @format GUID
   */
  paymentAcceptanceConfigurationId: string;
}

export interface GetPaymentAcceptanceConfigurationResponse {
  /** The requested PaymentAcceptanceConfiguration. */
  paymentAcceptanceConfiguration?: PaymentAcceptanceConfiguration;
}

export interface UpdatePaymentAcceptanceConfigurationRequest {
  /** PaymentAcceptanceConfiguration to be updated, may be partial. */
  paymentAcceptanceConfiguration: PaymentAcceptanceConfiguration;
}

export interface UpdatePaymentAcceptanceConfigurationResponse {
  /** Updated PaymentAcceptanceConfiguration. */
  paymentAcceptanceConfiguration?: PaymentAcceptanceConfiguration;
}

export interface AddPaymentAcceptanceConfigurationRulesRequest {
  /**
   * Id of payment acceptance configuration to update
   * @format GUID
   */
  paymentAcceptanceConfigurationId: string;
  /** Payment acceptance configuration revision */
  paymentAcceptanceConfigurationRevision?: string | null;
  /**
   * Rules to add
   * @minSize 1
   * @maxSize 1000
   */
  rules: PaymentAcceptanceRule[];
}

export interface AddPaymentAcceptanceConfigurationRulesResponse {
  /** Updated PaymentAcceptanceConfiguration */
  paymentAcceptanceConfiguration?: PaymentAcceptanceConfiguration;
}

export interface RemovePaymentAcceptanceConfigurationRulesRequest {
  /**
   * Id of payment acceptance configuration to update
   * @format GUID
   */
  paymentAcceptanceConfigurationId: string;
  /** Payment acceptance configuration revision */
  paymentAcceptanceConfigurationRevision?: string | null;
  /**
   * IDs of the rules to remove
   * @format GUID
   * @minSize 1
   * @maxSize 1000
   */
  ruleIds: string[];
}

export interface RemovePaymentAcceptanceConfigurationRulesResponse {
  /** Updated PaymentAcceptanceConfiguration */
  paymentAcceptanceConfiguration?: PaymentAcceptanceConfiguration;
}

export interface ModifyPaymentAcceptanceConfigurationRulesRequest {
  /**
   * Id of payment acceptance configuration to update
   * @format GUID
   */
  paymentAcceptanceConfigurationId: string;
  /** Payment acceptance configuration revision */
  paymentAcceptanceConfigurationRevision: string | null;
  /**
   * Rules to add
   * @maxSize 1000
   */
  rulesToAdd?: PaymentAcceptanceRule[];
  /**
   * IDs of the rules to remove
   * @format GUID
   * @maxSize 1000
   */
  rulesToRemoveIds?: string[];
  /**
   * Rules to update (partial updates using field masks)
   * @maxSize 1000
   */
  maskedRulesToUpdate?: MaskedRules[];
}

export interface MaskedRules {
  /** PaymentAcceptanceRule to be updated, may be partial */
  rule?: PaymentAcceptanceRule;
  /** Explicit list of fields to update. */
  fieldMask?: string[];
}

export interface ModifyPaymentAcceptanceConfigurationRulesResponse {
  /** Updated PaymentAcceptanceConfiguration */
  paymentAcceptanceConfiguration?: PaymentAcceptanceConfiguration;
}

export interface DeletePaymentAcceptanceConfigurationRequest {
  /**
   * Id of payment acceptance configuration to delete
   * @format GUID
   */
  paymentAcceptanceConfigurationId: string;
}

export interface DeletePaymentAcceptanceConfigurationResponse {}

export interface QueryPaymentAcceptanceConfigurationsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPaymentAcceptanceConfigurationsResponse {
  /** List of PaymentAcceptanceConfigurations. */
  paymentAcceptanceConfigurations?: PaymentAcceptanceConfiguration[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsRequest {
  /**
   * List of NileProtoTagsEntities that their tags will update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdatePaymentAcceptanceConfigurationTagsResult[];
  /** Metadata regarding the bulk update operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsResult {
  /** Metadata regarding the specific single update operation */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsByFilterRequest {
  /** Filter */
  filter: Record<string, any> | null;
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsByFilterResponse {
  /**
   * Job ID
   * @format GUID
   */
  jobId?: string;
}

export interface Empty {}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
  /**
   * Symphony — a siteless site representing a project within Symphony (the evolution of Orion).
   * Holds project data, conversations, assets, and manages collaborators / shared team members for the project.
   */
  SYMPHONY = 'SYMPHONY',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS'
  | 'SYMPHONY';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Creates a PaymentAcceptanceConfiguration.
 * @param paymentAcceptanceConfiguration - PaymentAcceptanceConfiguration to be created.
 * @internal
 * @documentationMaturity preview
 * @requiredField paymentAcceptanceConfiguration
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_CREATE
 * @returns The created PaymentAcceptanceConfiguration.
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.CreatePaymentAcceptanceConfiguration
 */
export async function createPaymentAcceptanceConfiguration(
  paymentAcceptanceConfiguration: PaymentAcceptanceConfiguration
): Promise<
  NonNullablePaths<
    PaymentAcceptanceConfiguration,
    | `rules`
    | `rules.${number}.paymentMethodTypeId`
    | `rules.${number}.accountConnectionId`
    | `rules.${number}.merchantEnabled`
    | `tags.tags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfiguration: paymentAcceptanceConfiguration,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.createPaymentAcceptanceConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.paymentAcceptanceConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { paymentAcceptanceConfiguration: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['paymentAcceptanceConfiguration']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a PaymentAcceptanceConfiguration.
 * @param paymentAcceptanceConfigurationId - ID of the PaymentAcceptanceConfiguration to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField paymentAcceptanceConfigurationId
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_READ
 * @returns The requested PaymentAcceptanceConfiguration.
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.GetPaymentAcceptanceConfiguration
 */
export async function getPaymentAcceptanceConfiguration(
  paymentAcceptanceConfigurationId: string
): Promise<
  NonNullablePaths<
    PaymentAcceptanceConfiguration,
    | `rules`
    | `rules.${number}.paymentMethodTypeId`
    | `rules.${number}.accountConnectionId`
    | `rules.${number}.merchantEnabled`
    | `tags.tags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfigurationId: paymentAcceptanceConfigurationId,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.getPaymentAcceptanceConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.paymentAcceptanceConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { paymentAcceptanceConfigurationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['paymentAcceptanceConfigurationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a PaymentAcceptanceConfiguration.
 *
 *
 * Each time the PaymentAcceptanceConfiguration is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the PaymentAcceptanceConfiguration.
 * This ensures you're working with the latest PaymentAcceptanceConfiguration
 * and prevents unintended overwrites.
 * @param _id - Id
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField paymentAcceptanceConfiguration
 * @requiredField paymentAcceptanceConfiguration.revision
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_UPDATE
 * @returns Updated PaymentAcceptanceConfiguration.
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.UpdatePaymentAcceptanceConfiguration
 */
export async function updatePaymentAcceptanceConfiguration(
  _id: string,
  paymentAcceptanceConfiguration: NonNullablePaths<
    UpdatePaymentAcceptanceConfiguration,
    `revision`,
    2
  >
): Promise<
  NonNullablePaths<
    PaymentAcceptanceConfiguration,
    | `rules`
    | `rules.${number}.paymentMethodTypeId`
    | `rules.${number}.accountConnectionId`
    | `rules.${number}.merchantEnabled`
    | `tags.tags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfiguration: {
      ...paymentAcceptanceConfiguration,
      id: _id,
    },
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.updatePaymentAcceptanceConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.paymentAcceptanceConfiguration!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { paymentAcceptanceConfiguration: '$[1]' },
        explicitPathsToArguments: {
          'paymentAcceptanceConfiguration.id': '$[0]',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'paymentAcceptanceConfiguration']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePaymentAcceptanceConfiguration {
  /**
   * Id
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * App id
   * @format GUID
   * @immutable
   */
  appId?: string | null;
  /**
   * Rules
   * @maxSize 1000
   */
  rules?: PaymentAcceptanceRule[];
  /**
   * Revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Date when PaymentAcceptanceConfiguration was created
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Date when PaymentAcceptanceConfiguration was updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

/**
 * Add PaymentAcceptanceConfiguration Rules
 * as result new rules will be validated and added to existing ones
 * @param paymentAcceptanceConfigurationId - Id of payment acceptance configuration to update
 * @internal
 * @documentationMaturity preview
 * @requiredField options.rules
 * @requiredField paymentAcceptanceConfigurationId
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_UPDATE
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.AddPaymentAcceptanceConfigurationRules
 */
export async function addPaymentAcceptanceConfigurationRules(
  paymentAcceptanceConfigurationId: string,
  options?: NonNullablePaths<
    AddPaymentAcceptanceConfigurationRulesOptions,
    `rules`,
    2
  >
): Promise<
  NonNullablePaths<
    AddPaymentAcceptanceConfigurationRulesResponse,
    | `paymentAcceptanceConfiguration.rules`
    | `paymentAcceptanceConfiguration.rules.${number}.paymentMethodTypeId`
    | `paymentAcceptanceConfiguration.rules.${number}.accountConnectionId`
    | `paymentAcceptanceConfiguration.rules.${number}.merchantEnabled`
    | `paymentAcceptanceConfiguration.tags.tags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfigurationId: paymentAcceptanceConfigurationId,
    paymentAcceptanceConfigurationRevision:
      options?.paymentAcceptanceConfigurationRevision,
    rules: options?.rules,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.addPaymentAcceptanceConfigurationRules(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          paymentAcceptanceConfigurationId: '$[0]',
          paymentAcceptanceConfigurationRevision:
            '$[1].paymentAcceptanceConfigurationRevision',
          rules: '$[1].rules',
        },
        singleArgumentUnchanged: false,
      },
      ['paymentAcceptanceConfigurationId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddPaymentAcceptanceConfigurationRulesOptions {
  /** Payment acceptance configuration revision */
  paymentAcceptanceConfigurationRevision?: string | null;
  /**
   * Rules to add
   * @minSize 1
   * @maxSize 1000
   */
  rules: PaymentAcceptanceRule[];
}

/**
 * Remove PaymentAcceptanceConfiguration
 * as result existing rules will be removed from PaymentAcceptanceConfiguration
 * some extra rules may be deleted
 * @param paymentAcceptanceConfigurationId - Id of payment acceptance configuration to update
 * @internal
 * @documentationMaturity preview
 * @requiredField options.ruleIds
 * @requiredField paymentAcceptanceConfigurationId
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_UPDATE
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.RemovePaymentAcceptanceConfigurationRules
 */
export async function removePaymentAcceptanceConfigurationRules(
  paymentAcceptanceConfigurationId: string,
  options?: NonNullablePaths<
    RemovePaymentAcceptanceConfigurationRulesOptions,
    `ruleIds`,
    2
  >
): Promise<
  NonNullablePaths<
    RemovePaymentAcceptanceConfigurationRulesResponse,
    | `paymentAcceptanceConfiguration.rules`
    | `paymentAcceptanceConfiguration.rules.${number}.paymentMethodTypeId`
    | `paymentAcceptanceConfiguration.rules.${number}.accountConnectionId`
    | `paymentAcceptanceConfiguration.rules.${number}.merchantEnabled`
    | `paymentAcceptanceConfiguration.tags.tags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfigurationId: paymentAcceptanceConfigurationId,
    paymentAcceptanceConfigurationRevision:
      options?.paymentAcceptanceConfigurationRevision,
    ruleIds: options?.ruleIds,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.removePaymentAcceptanceConfigurationRules(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          paymentAcceptanceConfigurationId: '$[0]',
          paymentAcceptanceConfigurationRevision:
            '$[1].paymentAcceptanceConfigurationRevision',
          ruleIds: '$[1].ruleIds',
        },
        singleArgumentUnchanged: false,
      },
      ['paymentAcceptanceConfigurationId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemovePaymentAcceptanceConfigurationRulesOptions {
  /** Payment acceptance configuration revision */
  paymentAcceptanceConfigurationRevision?: string | null;
  /**
   * IDs of the rules to remove
   * @format GUID
   * @minSize 1
   * @maxSize 1000
   */
  ruleIds: string[];
}

/**
 * Modify PaymentAcceptanceConfiguration Rules
 * as result new rules will be validated and added to existing ones
 * rules to removed will be removed, may remove extra rules depending on app_id specific logic
 * some extra rules may be added
 * rules_to_update allows partial updates to existing rules using field masks
 * @param paymentAcceptanceConfigurationId - Id of payment acceptance configuration to update
 * @param paymentAcceptanceConfigurationRevision - Payment acceptance configuration revision
 * @internal
 * @documentationMaturity preview
 * @requiredField paymentAcceptanceConfigurationId
 * @requiredField paymentAcceptanceConfigurationRevision
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_UPDATE
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.ModifyPaymentAcceptanceConfigurationRules
 */
export async function modifyPaymentAcceptanceConfigurationRules(
  paymentAcceptanceConfigurationId: string,
  paymentAcceptanceConfigurationRevision: string,
  options?: ModifyPaymentAcceptanceConfigurationRulesOptions
): Promise<
  NonNullablePaths<
    ModifyPaymentAcceptanceConfigurationRulesResponse,
    | `paymentAcceptanceConfiguration.rules`
    | `paymentAcceptanceConfiguration.rules.${number}.paymentMethodTypeId`
    | `paymentAcceptanceConfiguration.rules.${number}.accountConnectionId`
    | `paymentAcceptanceConfiguration.rules.${number}.merchantEnabled`
    | `paymentAcceptanceConfiguration.tags.tags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfigurationId: paymentAcceptanceConfigurationId,
    paymentAcceptanceConfigurationRevision:
      paymentAcceptanceConfigurationRevision,
    rulesToAdd: options?.rulesToAdd,
    rulesToRemoveIds: options?.rulesToRemoveIds,
    maskedRulesToUpdate: options?.maskedRulesToUpdate,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.modifyPaymentAcceptanceConfigurationRules(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          paymentAcceptanceConfigurationId: '$[0]',
          paymentAcceptanceConfigurationRevision: '$[1]',
          rulesToAdd: '$[2].rulesToAdd',
          rulesToRemoveIds: '$[2].rulesToRemoveIds',
          maskedRulesToUpdate: '$[2].maskedRulesToUpdate',
        },
        singleArgumentUnchanged: false,
      },
      [
        'paymentAcceptanceConfigurationId',
        'paymentAcceptanceConfigurationRevision',
        'options',
      ]
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ModifyPaymentAcceptanceConfigurationRulesOptions {
  /**
   * Rules to add
   * @maxSize 1000
   */
  rulesToAdd?: PaymentAcceptanceRule[];
  /**
   * IDs of the rules to remove
   * @format GUID
   * @maxSize 1000
   */
  rulesToRemoveIds?: string[];
  /**
   * Rules to update (partial updates using field masks)
   * @maxSize 1000
   */
  maskedRulesToUpdate?: MaskedRules[];
}

/**
 * Delete a PaymentAcceptanceConfiguration
 * @param paymentAcceptanceConfigurationId - Id of payment acceptance configuration to delete
 * @internal
 * @documentationMaturity preview
 * @requiredField paymentAcceptanceConfigurationId
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_DELETE
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.DeletePaymentAcceptanceConfiguration
 */
export async function deletePaymentAcceptanceConfiguration(
  paymentAcceptanceConfigurationId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    paymentAcceptanceConfigurationId: paymentAcceptanceConfigurationId,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.deletePaymentAcceptanceConfiguration(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { paymentAcceptanceConfigurationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['paymentAcceptanceConfigurationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of PaymentAcceptanceConfigurations, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 PaymentAcceptanceConfigurations can be returned per request.
 *
 * To learn how to query PaymentAcceptanceConfigurations, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 * @public
 * @documentationMaturity preview
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_READ
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.QueryPaymentAcceptanceConfigurations
 */
export function queryPaymentAcceptanceConfigurations(): PaymentAcceptanceConfigurationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PaymentAcceptanceConfiguration,
    'CURSOR',
    QueryPaymentAcceptanceConfigurationsRequest,
    QueryPaymentAcceptanceConfigurationsResponse
  >({
    func: async (payload: QueryPaymentAcceptanceConfigurationsRequest) => {
      const reqOpts =
        ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.queryPaymentAcceptanceConfigurations(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (
      query: QueryPaymentAcceptanceConfigurationsRequest['query']
    ) => {
      const args = [query, {}] as [
        QueryPaymentAcceptanceConfigurationsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPaymentAcceptanceConfigurationsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.paymentAcceptanceConfigurations,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PaymentAcceptanceConfigurationsQueryResult
  extends QueryCursorResult {
  items: PaymentAcceptanceConfiguration[];
  query: PaymentAcceptanceConfigurationsQueryBuilder;
  next: () => Promise<PaymentAcceptanceConfigurationsQueryResult>;
  prev: () => Promise<PaymentAcceptanceConfigurationsQueryResult>;
}

export interface PaymentAcceptanceConfigurationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId',
    value: string
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'appId'
      | 'rules.paymentMethodTypeId'
      | 'rules.accountConnectionId'
      | 'rules.merchantEnabled'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => PaymentAcceptanceConfigurationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<PaymentAcceptanceConfigurationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.QueryPaymentAcceptanceConfigurations
 * @requiredField query
 */
export async function typedQueryPaymentAcceptanceConfigurations(
  query: PaymentAcceptanceConfigurationQuery
): Promise<
  NonNullablePaths<
    QueryPaymentAcceptanceConfigurationsResponse,
    `paymentAcceptanceConfigurations`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.queryPaymentAcceptanceConfigurations(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PaymentAcceptanceConfigurationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        'rules.accountConnectionId',
        'rules.merchantEnabled',
        'rules.paymentMethodTypeId'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: ['_createdDate', '_id', '_updatedDate', 'appId'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  PaymentAcceptanceConfiguration,
  PaymentAcceptanceConfigurationQuerySpec
>;
export type PaymentAcceptanceConfigurationQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      PaymentAcceptanceConfiguration,
      PaymentAcceptanceConfigurationQuerySpec,
      PaymentAcceptanceConfigurationQuery
    >(),
  },
};

/**
 * Synchronously update tags on multiple PaymentAcceptanceConfigurations, by list of PaymentAcceptanceConfigurations ids
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 * @param ids - List of NileProtoTagsEntities that their tags will update.
 * @internal
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_UPDATE_TAGS
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.BulkUpdatePaymentAcceptanceConfigurationTags
 */
export async function bulkUpdatePaymentAcceptanceConfigurationTags(
  ids: string[],
  options?: BulkUpdatePaymentAcceptanceConfigurationTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdatePaymentAcceptanceConfigurationTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ids: ids,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.bulkUpdatePaymentAcceptanceConfigurationTags(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ids: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['ids', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsOptions {
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple PaymentAcceptanceConfigurations, by provided filter
 * An empty filter will update all PaymentAcceptanceConfigurations
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 * @param filter - Filter
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId PAYMENTS.PAYMENT_ACCEPTANCE_CONFIGURATION_UPDATE_TAGS
 * @fqn wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.BulkUpdatePaymentAcceptanceConfigurationTagsByFilter
 */
export async function bulkUpdatePaymentAcceptanceConfigurationTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdatePaymentAcceptanceConfigurationTagsByFilterOptions
): Promise<
  NonNullablePaths<
    BulkUpdatePaymentAcceptanceConfigurationTagsByFilterResponse,
    `jobId`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfiguration.bulkUpdatePaymentAcceptanceConfigurationTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdatePaymentAcceptanceConfigurationTagsByFilterOptions {
  /** List of Tags to assign */
  assignTags?: Tags;
  /** List of Tags to unAssign */
  unassignTags?: Tags;
}
